/**
 * @fileoverview Shared constants + types for the landing page sections.
 *
 * Pulled out of the original `Landing/index.tsx` so each section file can
 * import the bits it needs (image paths, feature list, etc.) without
 * pulling in unrelated sibling sections. Pure data + types — no React
 * components live here.
 */
import type { LucideIcon } from 'lucide-react'
import { Activity, BarChart3, Bell, Cloud, ScrollText, ShieldCheck, Smartphone, Sparkles, Trophy, Users, Workflow } from 'lucide-react'

// Clean horizontal lockup — wordmark + cricket motif, no tagline.
// Sized to sit naturally in the nav and footer without needing a separate
// text label next to it.
export const LOGO_SRC = '/images/web_logo.png'

// Real admin-dashboard screenshot used as the hero visual — shows the
// "Run the season from one scoreboard" hero card so the page sells the
// product the moment it loads.
export const HERO_IMAGE = '/landingpage/hero.png'

// Four mobile screenshots, drives the device showcase further down the
// page. Order matters — each is annotated with the feature it best
// illustrates so the labels line up with the device tilt sequence.
export const MOBILE_SHOTS = [
  { src: '/landingpage/mobile1.jpeg', label: 'Live scoring',     caption: 'Ball-by-ball entry with subs, injuries and undo.' },
  { src: '/landingpage/mobile2.jpeg', label: 'Match scoring',    caption: 'Innings score, over breakdown and partnership at a glance.' },
  { src: '/landingpage/mobile3.jpeg', label: 'Stats & charts',   caption: 'Worm chart, Manhattan, run rates and team comparisons.' },
  { src: '/landingpage/mobile4.jpeg', label: 'Tournament hub',   caption: 'Fixtures, results, points table, bracket and officials.' },
]

export const APK_DOWNLOAD_PATH = '/apk/app-release.apk'

// Public store listings. These are the distribution route the Download CTA
// now points at; `APK_DOWNLOAD_PATH` above is kept because the hero CTA and
// the footer still offer the direct sideload build.
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.positron.futureprotea'
export const APP_STORE_URL =
  'https://apps.apple.com/in/app/future-protea/id6781806981'

export interface Feature {
  icon: LucideIcon
  title: string
  description: string
}

export const FEATURES: Feature[] = [
  {
    icon: Activity,
    title: 'Live ball-by-ball scoring',
    description:
      'Mobile scorers record every delivery — runs, wickets, extras, overthrows — and viewers see the scorecard update in real time via Socket.IO.',
  },
  {
    icon: Trophy,
    title: 'Tournaments & standings',
    description:
      'Run leagues, knockouts and round-robin tournaments. Points tables, NRR and brackets are computed automatically from every completed match.',
  },
  {
    icon: BarChart3,
    title: 'Player & team analytics',
    description:
      'Career stats, top performers, batting / bowling averages, strike rates, economy, wagon wheels and Manhattan charts out of the box.',
  },
  {
    icon: Bell,
    title: 'Push notifications',
    description:
      'Match starts, key moments, role changes, and admin announcements are delivered to every player, official and viewer via FCM in seconds.',
  },
  {
    icon: Users,
    title: 'Role-aware experience',
    description:
      'Admins, organisers, feeders, umpires, scorers, coaches, players and viewers each get a screen tailored to what they actually need to do.',
  },
  {
    icon: ShieldCheck,
    title: 'Secure by default',
    description:
      'JWT auth with refresh-token rotation, role-based permissions on every endpoint, and httpOnly cookies that XSS cannot read.',
  },
]

export interface Step {
  number: string
  icon: LucideIcon
  title: string
  description: string
}

export const STEPS: Step[] = [
  {
    number: '01',
    icon: Workflow,
    title: 'Set up your tournament',
    description:
      'Admins create teams, register players, schedule fixtures and assign umpires + scorers from the admin web. Tournaments can be league, knockout or hybrid.',
  },
  {
    number: '02',
    icon: Smartphone,
    title: 'Score on the field',
    description:
      'On match day, the assigned feeder opens the mobile app and records every delivery. Toss → playing XI → live scoring, all from one screen.',
  },
  {
    number: '03',
    icon: ScrollText,
    title: 'Share the result',
    description:
      'Viewers follow live, fans see the scorecard, admins get post-match summaries and PDFs. Standings, NRR and top performers update automatically.',
  },
]

export interface StatLine {
  label: string
  value: string
  icon: LucideIcon
}

export const STATS: StatLine[] = [
  { label: 'Roles supported', value: '8+', icon: Users },
  { label: 'Live match latency', value: '<1s', icon: Activity },
  { label: 'Offline scoring', value: 'Yes', icon: Cloud },
  { label: 'Open API', value: 'REST + WS', icon: Sparkles },
]
